/**
 * quoteCurrency.ts
 * Display-layer currency conversion + formatting for the Quote module
 * (BUG-2026-0120 #6).
 *
 * A quote is SINGLE-CURRENCY: the user picks CAD or USD via Quote.Currency.
 * All line-item prices and totals are STORED in CAD (the base currency) and the
 * quoteCalculator works exclusively in CAD. When a quote's Currency is not CAD,
 * the amounts are CONVERTED FOR DISPLAY ONLY using the quote's snapshotted
 * FxRateAtQuote — they are never re-entered or re-stored.
 *
 * FX direction (confirmed from the schema + helper, do NOT invert):
 *   ExchangeRatePeriod.RateToCAD = "how many CAD per 1 unit of Currency"
 *   (migration 052, line 12). getRateAt() returns RateToCAD directly and
 *   quoteService.create snapshots it into Quote.FxRateAtQuote. So
 *   FxRateAtQuote is CAD-per-unit-of-quote-currency (≈1.35 for USD).
 *
 *   To show a CAD-base amount in the quote currency:
 *     displayAmount = cadAmount / FxRateAtQuote
 *   e.g. $135 CAD at rate 1.35 → $100 USD. For CAD quotes the rate is 1.0, so
 *   the amount passes through unchanged.
 */

/** Normalize a currency code; defaults to CAD when missing/blank. */
function normalizeCurrency(currency: string | null | undefined): string {
  const c = (currency ?? '').trim().toUpperCase();
  return c || 'CAD';
}

/**
 * Convert a CAD-base amount into the quote's display currency.
 *
 * @param cadAmount     The stored CAD (base) amount.
 * @param currency      The quote currency ('CAD' | 'USD' | …).
 * @param fxRateAtQuote The snapshotted Quote.FxRateAtQuote (CAD per 1 unit of
 *                      currency). 1.0 for CAD. Null/0/invalid → treated as 1.0
 *                      (no conversion) so the UI never divides by zero or hides
 *                      a value; a missing rate is an admin-config issue surfaced
 *                      elsewhere, not a place to silently zero out the price.
 * @returns The amount expressed in `currency`.
 */
export function convertFromCad(
  cadAmount: number,
  currency: string | null | undefined,
  fxRateAtQuote: number | null | undefined,
): number {
  const cur = normalizeCurrency(currency);
  if (cur === 'CAD') return cadAmount;
  const rate = fxRateAtQuote;
  if (rate == null || !Number.isFinite(rate) || rate <= 0) return cadAmount;
  // RateToCAD is CAD-per-unit-of-currency, so divide to go CAD → currency.
  return cadAmount / rate;
}

/**
 * Human-readable description of the FX rate, matching RateToCAD semantics
 * (FxRateAtQuote = CAD per 1 unit of the quote currency). For a USD quote at
 * rate 1.41 this reads "1 USD = 1.4100 CAD". For CAD it reads
 * "1 CAD = 1.0000 CAD". Returns null when a non-CAD quote has no rate set yet
 * (the caller shows a "set it manually" hint instead).
 */
export function fxRateLabel(
  currency: string | null | undefined,
  fxRateAtQuote: number | null | undefined,
): string | null {
  const cur = normalizeCurrency(currency);
  if (cur === 'CAD') return '1 CAD = 1.0000 CAD';
  if (fxRateAtQuote == null || !Number.isFinite(fxRateAtQuote)) return null;
  return `1 ${cur} = ${fxRateAtQuote.toFixed(4)} CAD`;
}

/**
 * Convert (if needed) and format a CAD-base amount in the quote currency.
 * Uses the Intl currency style so the right symbol is shown (CA$ / US$ etc.).
 */
export function formatQuoteAmount(
  cadAmount: number,
  currency: string | null | undefined,
  fxRateAtQuote: number | null | undefined,
): string {
  const cur = normalizeCurrency(currency);
  const display = convertFromCad(cadAmount, currency, fxRateAtQuote);
  return display.toLocaleString('en-CA', { style: 'currency', currency: cur });
}
